import React, { useRef, useState } from 'react';
import { useReducedMotion } from '../hooks';
import { useRipples, RippleLayer } from './useRipples';
import './worlds.css';

/**
 * WorryRelease — "Let it go". A handful of soft "worry" orbs drift gently; tap one
 * (or press Enter) and it eases up and dissolves with a soft ripple, then a fresh
 * one drifts in a moment later. A simple, tactile way to let tension go, one tap
 * at a time. Orbs are buttons (keyboard-activatable). Under reduced motion the
 * drift stops but taps still release.
 */
interface Worry {
  id: number;
  left: number;
  bottom: number;
  size: number;
  hue: number;
  dur: number;
  delay: number;
  gone?: boolean;
}

const rnd = (a: number, b: number) => a + Math.random() * (b - a);
const makeWorry = (id: number): Worry => ({
  id,
  left: rnd(8, 82),
  bottom: rnd(16, 64),
  size: rnd(40, 76),
  hue: Math.round(rnd(196, 268)),
  dur: rnd(5, 9),
  delay: rnd(0, 2.5),
});

export const WorryRelease: React.FC = () => {
  const reduced = useReducedMotion();
  const seq = useRef(6);
  const [worries, setWorries] = useState<Worry[]>(() => Array.from({ length: 6 }, (_, i) => makeWorry(i)));
  const { ripples, spawnAtElement } = useRipples();

  const release = (worry: Worry, el: HTMLElement) => {
    if (worry.gone) return;
    spawnAtElement(el);
    setWorries((p) => p.map((w) => (w.id === worry.id ? { ...w, gone: true } : w)));
    // Remove after the dissolve animation, then drift a fresh one in.
    window.setTimeout(() => {
      setWorries((p) => p.filter((w) => w.id !== worry.id));
      const nid = seq.current++;
      window.setTimeout(() => setWorries((p) => (p.length < 6 ? [...p, makeWorry(nid)] : p)), 700);
    }, 680);
  };

  return (
    <div className={`mm-world mm-world--release ${reduced ? 'is-still' : ''}`}>
      {worries.map((w) => (
        <button
          key={w.id}
          type="button"
          className={`mm-worry ${w.gone ? 'is-gone' : ''}`}
          aria-label="Let this worry go"
          onClick={(e) => release(w, e.currentTarget)}
          style={{
            left: `${w.left}%`,
            bottom: `${w.bottom}%`,
            width: w.size,
            height: w.size,
            animationDuration: `${w.dur}s`,
            animationDelay: `${w.delay}s`,
            ['--hue' as string]: w.hue,
          }}
        />
      ))}
      <RippleLayer ripples={ripples} />
      <p className="mm-world__hint">Tap a worry to let it go</p>
    </div>
  );
};

export default WorryRelease;
